import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { UserPlus, User, ExternalLink, CheckCircle, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useCreateCandidate } from '@/hooks/mutations/useCreateCandidate';
import { supabase } from '@/integrations/supabase/client';
import type { NewCandidateFormData } from '@/components/candidates/types';
import { extractCandidateInfoFromSession } from '@/utils/screeningNameExtraction';

interface AddCandidateFromScreeningProps {
  session: {
    id: string;
    transcript: string;
    phone_number?: string;
    candidate_id?: string;
    ai_summary?: any;
    candidates?: {
      id: string;
      first_name: string;
      last_name: string;
      email: string;
      phone: string;
      candidate_number: string;
    };
  };
}

// Legacy extract skills from transcript
const extractSkillsFromTranscript = (transcript: string): string[] => {
  const lines = transcript.split('\n');
  const skills: string[] = [];
  const skillKeywords = ['JavaScript', 'React', 'Python', 'Java', 'TypeScript', 'Node.js', 'SQL', 'AWS', 'Docker', 'Kubernetes'];
  
  for (const line of lines) {
    for (const skill of skillKeywords) {
      if (line.toLowerCase().includes(skill.toLowerCase()) && !skills.includes(skill)) {
        skills.push(skill);
      }
    }
  }
  return skills;
};

export const AddCandidateFromScreening: React.FC<AddCandidateFromScreeningProps> = ({ session }) => {
  const navigate = useNavigate();
  const { createCandidate, isCreatingCandidate } = useCreateCandidate();
  const [isAdded, setIsAdded] = useState(false);
  
  // Check if candidate already exists for this session
  const hasExistingCandidate = session.candidate_id && session.candidates;
  
  // Extract candidate info using enhanced utility that prioritizes AI summary
  const extracted = extractCandidateInfoFromSession(session);
  const skills = session.transcript ? extractSkillsFromTranscript(session.transcript) : [];
  
  const [candidateData, setCandidateData] = useState({
    first_name: extracted.firstName,
    last_name: extracted.lastName,
    email: extracted.email,
    phone: session.phone_number || '',
    skills: skills
  });

  const handleAddCandidate = () => {
    if (!candidateData.first_name || !candidateData.last_name) {
      toast.error('Please enter at least first and last name');
      return;
    }

    const newCandidateData: NewCandidateFormData = {
      first_name: candidateData.first_name,
      last_name: candidateData.last_name,
      email: candidateData.email || '',
      phone: candidateData.phone || null,
      source: 'Phone Screening',
      status: 'screened',
    };

    createCandidate(newCandidateData, {
      onSuccess: async (candidate) => {
        // Now update the candidate with screening data and skills
        await updateCandidateWithScreeningData(candidate.id);
        setIsAdded(true);
        toast.success('Candidate added successfully with screening data!');
      }
    });
  };

  const updateCandidateWithScreeningData = async (candidateId: string) => {
    try {
      const { error } = await supabase
        .from('candidates')
        .update({
          screening_session_id: session.id,
          screening_transcript: session.transcript,
          screening_date: new Date().toISOString(),
          skills: candidateData.skills,
          notes: `Initial screening notes from call on ${new Date().toLocaleDateString()}:\n\nExtracted from screening conversation.\n\nPhone: ${session.phone_number || 'Not provided'}\n\nExtracted Skills: ${candidateData.skills.join(', ')}`
        })
        .eq('id', candidateId);

      if (error) {
        console.error('Error updating candidate with screening data:', error);
        toast.error('Candidate created but failed to save screening data');
      }
    } catch (error) {
      console.error('Error updating candidate with screening data:', error);
    }
  };

  if (isAdded) {
    return (
      <Card className="border-green-200 bg-green-50">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center gap-2 text-green-700">
            <Check className="h-5 w-5" />
            <span className="font-medium">Candidate added successfully!</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {hasExistingCandidate ? (
            <>
              <CheckCircle className="h-5 w-5 text-green-500" />
              Candidate Added
            </>
          ) : (
            <>
              <UserPlus className="h-5 w-5" />
              Add as New Candidate
            </>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {hasExistingCandidate ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg bg-green-50 dark:bg-green-950/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="font-medium">
                    {session.candidates?.first_name} {session.candidates?.last_name}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {session.candidates?.candidate_number} • {session.candidates?.email}
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/candidates/${session.candidate_id}`)}
                className="flex items-center gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                View Candidate
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              This screening session is already linked to an existing candidate in your database.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={candidateData.first_name}
                  onChange={(e) => setCandidateData(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder="Enter first name"
                />
              </div>
              <div>
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={candidateData.last_name}
                  onChange={(e) => setCandidateData(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Enter last name"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={candidateData.email}
                onChange={(e) => setCandidateData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Enter email address"
              />
            </div>
            
            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                value={candidateData.phone}
                onChange={(e) => setCandidateData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="Enter phone number"
              />
            </div>

            {candidateData.skills.length > 0 && (
              <div>
                <Label>Extracted Skills</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {candidateData.skills.map((skill, index) => (
                    <Badge key={index} variant="secondary">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <Button 
              onClick={handleAddCandidate}
              disabled={isCreatingCandidate || !candidateData.first_name || !candidateData.last_name}
              className="w-full"
            >
              {isCreatingCandidate ? "Adding..." : "Add Candidate"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};